import { api } from './api';

export interface UserTimeReportEntry {
  user_id: number;
  user_name: string;
  entry_date: string;
  total_duration_seconds: number;
  duration_formatted: string;
}

export interface TimeEntry {
  id: number;
  activity: string;
  case_title: string;
  user_name: string;
  started_at: string;
  stopped_at: string;
  duration_seconds: number;
  duration_formatted: string;
  notes: string | null;
}

export interface DayTimeSheet {
  date: string;
  day_of_week: string;
  formatted_date: string;
  entries: TimeEntry[];
  total_duration_seconds: number;
  total_duration_formatted: string;
}

export interface UserTimeSheet {
  user: {
    id: number;
    name: string;
  };
  date_range: {
    start: string;
    end: string;
  };
  days: DayTimeSheet[];
}

export interface VendorAssignmentReportEntry {
  vendor_user: string;
  vendor_case_id: number | null;
  vendor_firm_id: number | null;
  title: string;
  subtitle: string | null;
  project: string;
  file_number: string | null;
  status: string | null;
  status_label: string;
  client_case_id: number | null;
  client_case_title: string | null;
  client_firm_id: number | null;
  estimate: {
    id: number;
    quantity: number;
    billing_type: string;
    billing_type_label: string;
  } | null;
  rate: string;
  rate_value: number | null;
  amount: string;
  amount_value: number | null;
  finalize_date: string | null;
  assigned_at: string | null;
}

export interface TimeTrackerReportEntry {
  timer_id: number;
  case_id: number;
  case_name: string;
  file_number: string | null;
  client: string;
  client_firm_id: number | null;
  activity_name: string;
  disposition: string;
  user: string;
  user_id: number;
  user_email: string | null;
  date: string;
  date_formatted: string;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  duration_formatted: string;
  duration_hours: number;
  deduction_seconds: number;
  deduction_formatted: string;
  deduction_hours: number;
  net_duration_seconds: number;
  net_duration_formatted: string;
  ref_id: string;
  note: string;
  is_finalized: boolean;
  is_invoiced: boolean;
}

export interface TimeTrackerReportFilters {
  client_firms: Array<{ id: number; name: string }>;
  users: Array<{ id: number; name: string; email: string }>;
  activities: Array<{ name: string; value: string }>;
  dispositions: Array<{ name: string; value: string }>;
}

export interface ActiveTimerReportEntry {
  session_id: number;
  case_id: number;
  case_name: string;
  file_number: string | null;
  client: string;
  client_firm_id: number | null;
  user: string;
  user_id: number;
  user_email: string | null;
  started_at: string;
  started_at_formatted: string;
  elapsed_seconds: number;
  elapsed_formatted: string;
  elapsed_hours: number;
}

export interface UserCheckinReportEntry {
  user_id: number;
  user_name: string;
  checkin_date: string;
  checkin_start: number;
  checkin_start_est: string;
  checkin_start_ist: string;
  checkin_end: number;
  checkin_end_est: string;
  checkin_end_ist: string;
  break_time: number;
  break_time_formatted: string;
  duration: number;
  duration_formatted: string;
  login_hours: number;
  login_hours_formatted: string;
}

export const reportService = {
  getUserTimeReport: async (params: {
    user_id?: number;
    start_date: string;
    end_date: string;
  }): Promise<UserTimeReportEntry[]> => {
    const response = await api.get('/reports/user-time', { params });
    return response.data.data;
  },

  getUserTimeSheet: async (params: {
    user_id: number;
    start_date: string;
    end_date: string;
  }): Promise<UserTimeSheet> => {
    const response = await api.get('/reports/user-time-sheet', { params });
    return response.data.data;
  },

  getVendorAssignmentReport: async (params: {
    filter_title?: string;
    filter_subtitle?: string;
    start_date?: string;
    end_date?: string;
    project_status?: string;
    vendor_firm_id?: number;
  }): Promise<VendorAssignmentReportEntry[]> => {
    const response = await api.get('/reports/vendor-assignment', { params });
    return response.data.data;
  },

  getTimeTrackerReport: async (params: {
    client_firm_id?: number;
    user_id?: number;
    start_date?: string;
    end_date?: string;
    activity?: string | string[];
    disposition?: string | string[];
    export?: 'csv' | 'excel';
  }): Promise<TimeTrackerReportEntry[] | Blob> => {
    const response = await api.get('/reports/time-tracker', { 
      params,
      responseType: params.export ? 'blob' : 'json'
    });
    if (params.export) {
      return response.data as Blob;
    }
    return response.data.data;
  },

  getTimeTrackerReportFilters: async (): Promise<TimeTrackerReportFilters> => {
    const response = await api.get('/reports/time-tracker/filters');
    return response.data.data;
  },

  getActiveTimerReport: async (params: {
    client_firm_id?: number;
    user_id?: number;
  }): Promise<ActiveTimerReportEntry[]> => {
    const response = await api.get('/reports/active-timers', { params });
    return response.data.data;
  },

  getUserCheckinReport: async (params: {
    user_id?: number;
    start_date: string;
    end_date: string;
  }): Promise<UserCheckinReportEntry[]> => {
    const response = await api.get('/reports/user-checkin', { params });
    return response.data.data;
  },

  /** Idle Time Activity Report: inactivity nudge logs with gaps between entries. PMP & Site Admin only. */
  getIdleTimeActivityReport: async (params: {
    user_id?: number;
    start_date: string;
    end_date: string;
  }): Promise<IdleTimeActivityReportEntry[]> => {
    const response = await api.get('/reports/idle-time-activity', { params });
    return response.data.data;
  },
};

export interface IdleTimerGap {
  from: string;
  to: string;
  minutes: number;
  formatted: string;
}

export interface IdleTimeActivityReportEntry {
  user_id: number;
  user_name: string;
  date: string;
  nudge_shown_count: number;
  nudge_continue_count: number;
  nudge_pause_count: number;
  timer_session_count: number;
  first_timer_start: string | null;
  last_timer_end: string | null;
  total_timer_seconds: number;
  total_timer_minutes: number;
  total_timer_formatted: string;
  gap_count: number;
  total_gap_minutes: number;
  total_gap_formatted: string;
  gaps: IdleTimerGap[];
}

export default reportService;

